// Phase 2 P2-1 wiring — useDeskToday hook.
//
// Read-only access to today's THE DESK output:
//   - desk_picks         — specialist selections from published product tiers
//   - desk_clash_flags   — auto-detected cross-domain convergence
//   - desk_formulas_today — Standing Ten / Chaos Day / Unseen / Anchor / Contrarian
//
// All three sources are public reads (RLS allows authenticated select).
// The hook never writes. The zero-cost 11:00 GMT selector publishes; this hook displays.
//
// Types are inlined here because the Database types haven't been regenerated
// to include the new tables. Once `npm run db:gen-types` is part of the
// build, swap to Database["public"]["Tables"]["desk_picks"]["Row"] etc.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

// ─── Row types (mirror the SQL schemas) ────────────────────────────

// "lean" / "read" are non-bet model-read tiers (no game wasted): a sub-BET
// edge to monitor (lean) or a pure model line (read). They never carry a BET
// recommendation and are stored with status="read".
export type ConvictionTier = "standard" | "high" | "maximum" | "lean" | "read";
export type DeskPickStatus = "submitted" | "abstain" | "published" | "rejected" | "read";

export interface DeskPickRow {
  id: string;
  desk_date: string;
  agent_id: number;
  agent_domain: string;
  fixture_id: string | null;
  market: string | null;
  selection: string | null;
  odds_at_submission: number | null;
  confidence_score: number | null;
  ev_estimate: number | null;
  conviction_tier: ConvictionTier | null;
  primary_dna_factors: string[] | null;
  clv_projection_pp: number | null;
  platform_recommendation: string | null;
  reasoning: string | null;
  source_product: string | null;
  source_tier: string | null;
  source_agreement_count: number;
  status: DeskPickStatus;
  abstain_reason: string | null;
  submitted_at: string;
  immutable_at: string | null;
  // DESK-UI-1: resolved fixture context (joined client-side from
  // canonical_fixtures + teams + competitions). Null until resolved /
  // for abstentions with no fixture_id.
  home_team: string | null;
  away_team: string | null;
  competition: string | null;
  kickoff_at: string | null;
}

export interface DeskClashFlagRow {
  desk_date: string;
  fixture_id: string;
  agent_count: number;
  agent_ids: number[];
  agent_domains: string[];
  selections: string[];
  identical_pick: boolean;
}

export type DeskFormulaName =
  | "standing_ten"
  | "chaos_day"
  | "unseen"
  | "the_anchor"
  | "the_contrarian";

export interface DeskFormulaTodayRow {
  formula_name: DeskFormulaName;
  publication_status: "published" | "skipped_no_qualifiers" | "rejected";
  leg_count: number;
  combined_odds: number | null;
  skip_reason: string | null;
  cross_formula_overlap: string[] | null;
  built_at: string;
  immutable_at: string | null;
  legs: Array<{
    pick_id: string;
    agent_id: number;
    agent_domain: string;
    fixture_id: string;
    home_team: string | null;
    away_team: string | null;
    competition: string | null;
    kickoff_at: string | null;
    market: string;
    selection: string;
    odds: number;
    confidence: number;
    ev: number;
    conviction_tier: ConvictionTier;
    reasoning: string | null;
    platform: string | null;
  }>;
}

// ─── Hook state ────────────────────────────────────────────────────

export interface DeskTodayState {
  picks: DeskPickRow[];
  clashFlags: DeskClashFlagRow[];
  formulas: DeskFormulaTodayRow[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const todayIso = (): string => new Date().toISOString().slice(0, 10);

export function useDeskToday(): DeskTodayState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [picks, setPicks] = useState<DeskPickRow[]>([]);
  const [clashFlags, setClashFlags] = useState<DeskClashFlagRow[]>([]);
  const [formulas, setFormulas] = useState<DeskFormulaTodayRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    if (!user) {
      setPicks([]);
      setClashFlags([]);
      setFormulas([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    const today = todayIso();

    // Generated Database types don't yet include the new desk_* tables/views.
    // Cast the client to `any` for these new reads. Once `npm run db:gen-types`
    // is run after migrations, this cast can be removed.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sb = supabase as any;

    const [picksRes, flagsRes, formulasRes] = await Promise.all([
      sb
        .from("desk_picks")
        .select(
          "id, desk_date, agent_id, agent_domain, fixture_id, market, selection, odds_at_submission, confidence_score, ev_estimate, conviction_tier, primary_dna_factors, clv_projection_pp, platform_recommendation, reasoning, source_product, source_tier, source_agreement_count, status, abstain_reason, submitted_at, immutable_at",
        )
        .eq("desk_date", today)
        .order("agent_id", { ascending: true }),
      sb
        .from("desk_clash_flags")
        .select("*")
        .eq("desk_date", today)
        .order("agent_count", { ascending: false }),
      sb
        .from("desk_formulas_today")
        .select(
          "formula_name, publication_status, leg_count, combined_odds, skip_reason, cross_formula_overlap, built_at, immutable_at, legs",
        ),
    ]);

    const errMessages = [picksRes.error, flagsRes.error, formulasRes.error]
      .filter((e: { message?: string } | null): e is { message: string } => Boolean(e && e.message))
      .map((e) => e.message);
    if (errMessages.length > 0) {
      setError(errMessages.join(" · "));
    }

    const rawPicks = (picksRes.data ?? []) as unknown as DeskPickRow[];

    // DESK-UI-1: resolve fixture_id → human-readable match context. The
    // desk_picks table stores only the text fixture_id (tha_*/api_football:*),
    // so we join canonical_fixtures + teams + competitions client-side and
    // merge home/away/competition/kickoff onto each pick. Abstentions (no
    // fixture_id) pass through with nulls.
    const fixtureIds = Array.from(
      new Set(rawPicks.map((p) => p.fixture_id).filter((id): id is string => Boolean(id))),
    );
    let fixtureMap = new Map<
      string,
      {
        home_team: string | null;
        away_team: string | null;
        competition: string | null;
        kickoff_at: string | null;
      }
    >();
    if (fixtureIds.length > 0) {
      const fxRes = await sb
        .from("canonical_fixtures")
        .select(
          "fixture_id, kickoff_at, home:teams!canonical_fixtures_home_team_id_fkey(canonical_name), away:teams!canonical_fixtures_away_team_id_fkey(canonical_name), competition:competitions!canonical_fixtures_competition_id_fkey(canonical_name)",
        )
        .in("fixture_id", fixtureIds);
      if (!fxRes.error && Array.isArray(fxRes.data)) {
        fixtureMap = new Map(
          (fxRes.data as any[]).map((f) => [
            f.fixture_id as string,
            {
              home_team: f.home?.canonical_name ?? null,
              away_team: f.away?.canonical_name ?? null,
              competition: f.competition?.canonical_name ?? null,
              kickoff_at: typeof f.kickoff_at === "string" ? f.kickoff_at : null,
            },
          ]),
        );
      }
    }
    const enrichedPicks: DeskPickRow[] = rawPicks.map((p) => {
      const fx = p.fixture_id ? fixtureMap.get(p.fixture_id) : undefined;
      return {
        ...p,
        home_team: fx?.home_team ?? null,
        away_team: fx?.away_team ?? null,
        competition: fx?.competition ?? null,
        kickoff_at: fx?.kickoff_at ?? null,
      };
    });

    // Drop picks for matches that have already kicked off — a started game is no
    // longer an actionable pre-match pick. Abstentions and picks with no resolved
    // kickoff are kept (no fixture / no time → can't prove it started).
    const nowMs = Date.now();
    const upcomingPicks = enrichedPicks.filter((p) => {
      if (!p.kickoff_at) return true;
      const t = Date.parse(p.kickoff_at);
      return Number.isNaN(t) ? true : t >= nowMs;
    });

    setPicks(upcomingPicks);
    setClashFlags((flagsRes.data ?? []) as unknown as DeskClashFlagRow[]);
    setFormulas((formulasRes.data ?? []) as unknown as DeskFormulaTodayRow[]);
    setLoading(false);
  }, [supabase, user]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return useMemo<DeskTodayState>(
    () => ({ picks, clashFlags, formulas, loading, error, refetch }),
    [picks, clashFlags, formulas, loading, error, refetch],
  );
}
